import React, { useState, useEffect, useCallback, useMemo } from "react";
import downArrow from "../../../assets/design-revamp-product/product-icons/product-arrow.svg";
import addIcon from "../../../assets/design-revamp-product/product-icons/add-icon.svg";
import "../../NewProduct/index.css";
import "../index.css";

const styles = `
  .variations-row {
    display: flex;
    gap: 16px;
  }

  .remove-button {
    background: none;
    border: none;
    color: #dc3545;
    cursor: pointer;
    padding: 4px;
    font-size: 16px;
    margin-left: 5px;
  }

  .remove-button:hover {
    color: #bb2d3b;
  }

  .relative {
    position: relative;
  }
`;

const ProductSubgroupDropdown = ({ subgroups, initialSubgroupId, onClick }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(false);

  // Find initial subgroup from the subgroups list
  const initialSubgroup = useMemo(
    () => subgroups?.find((sg) => sg.id === initialSubgroupId),
    [subgroups, initialSubgroupId]
  );

  const [selectedSubgroup, setSelectedSubgroup] = useState(initialSubgroup);

  useEffect(() => {
    // Only set the initial subgroup if it exists and we haven't selected one yet
    if (initialSubgroup && !selectedSubgroup) {
      setSelectedSubgroup(initialSubgroup);
      onClick(initialSubgroup);
    }
  }, [initialSubgroup, selectedSubgroup, onClick]);

  const visibleItems = isExpanded ? subgroups : subgroups?.slice(0, 4);

  const handleSubgroupSelect = (subgroup) => {
    setSelectedSubgroup(subgroup);
    onClick(subgroup);
  };

  return (
    <div className="px-1" style={{ minWidth: "max-content" }}>
      <div
        className="product-dropdown-top"
        onClick={() => setOpenDropdown(!openDropdown)}
      >
        <h3
          style={{
            fontSize: "14px",
            width: "85%",
            whiteSpace: "nowrap",
            marginRight: "10px",
          }}
        >
          {selectedSubgroup?.name || "Category"}
        </h3>
        <img src={downArrow} width={15} height={15} alt="Down Arrow Dropdown" />
      </div>
      {openDropdown && (
        <div className="product-dropdown-body">
          <div>
            {visibleItems?.map((item) => (
              <div
                key={item.id}
                onClick={() => handleSubgroupSelect(item)}
                className="product-dropdown-items"
              >
                <span className="text-sm">{item.name}</span>
              </div>
            ))}
          </div>

          {subgroups?.length > 4 && (
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="mt-2 product-dropdown-select-more"
            >
              <img
                src={downArrow}
                alt="Down Arrow Dropdown"
                width={15}
                height={15}
              />
            </button>
          )}
        </div>
      )}
    </div>
  );
};

const VariationSelector = ({
  onAddVariation,
  variations,
  onRemoveVariation,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [newVariationName, setNewVariationName] = useState("");

  const handleAdd = () => {
    if (newVariationName.trim()) {
      onAddVariation({
        name: newVariationName,
        values: [],
      });
      setNewVariationName("");
      setIsOpen(false);
    }
  };

  return (
    <div>
      <div className="product-dropdown-top relative">
        <div className="variation-header" onClick={() => setIsOpen(!isOpen)}>
          <h3
            style={{
              fontSize: "14px",
              width: "85%",
              whiteSpace: "nowrap",
              marginRight: "10px",
            }}
          >
            Variation
          </h3>
          <img
            src={downArrow}
            width={15}
            height={15}
            alt="Down Arrow Dropdown"
          />
        </div>
      </div>

      {isOpen && (
        <>
          <div className="product-dropdown-body">
            <div>
              {variations.map((item, index) => (
                <div
                  key={typeof item === "object" ? item.id : index}
                  className="product-dropdown-items"
                >
                  <span>{typeof item === "object" ? item.name : item}</span>

                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onRemoveVariation(index);
                    }}
                    className="remove-button"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
            <div className="product-dropdown-add-item relative">
              <input
                type="text"
                value={newVariationName}
                onChange={(e) => setNewVariationName(e.target.value)}
                placeholder="Add: 'System'"
                className="variation-input"
              />
              <img
                src={addIcon}
                alt="Add Product Variant Icon"
                width={18}
                height={18}
                style={{ position: "absolute", right: "5px" }}
                onClick={handleAdd}
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
};

const VariationValues = ({ variation, onUpdateValues }) => {
  const [newValue, setNewValue] = useState("");
  const [openDropdownValue, setOpenDropdownValue] = useState(false);

  const addValue = () => {
    if (newValue.trim()) {
      onUpdateValues({
        ...variation,
        values: [...variation.values, newValue],
      });
      setNewValue("");
    }
  };

  const removeValue = (indexToRemove) => {
    onUpdateValues({
      ...variation,
      values: variation.values.filter((_, index) => index !== indexToRemove),
    });
  };

  // Helper function to safely get the display value
  const getDisplayValue = (value) => {
    if (typeof value === "string") return value;
    if (typeof value === "object" && value !== null) {
      return value.value || value.name || JSON.stringify(value);
    }
    return String(value);
  };

  return (
    <div className="variation-box">
      <div
        className="product-dropdown-top relative"
        onClick={() => setOpenDropdownValue(!openDropdownValue)}
      >
        <div className="variation-header">
          <h3
            style={{
              fontSize: "14px",
              width: "85%",
              whiteSpace: "nowrap",
              marginRight: "10px",
            }}
          >
            {variation.name}
          </h3>
          <img
            src={downArrow}
            width={15}
            height={15}
            alt="Down Arrow Dropdown"
          />
        </div>
      </div>

      {openDropdownValue && (
        <>
          <div className="product-dropdown-body">
            {variation.values.map((value, index) => (
              <div key={index} className="product-dropdown-items">
                <span>{getDisplayValue(value)}</span>
                <button
                  onClick={() => removeValue(index)}
                  className="remove-button"
                >
                  ×
                </button>
              </div>
            ))}
            <div className="product-dropdown-add-item relative">
              <input
                type="text"
                value={newValue}
                onChange={(e) => setNewValue(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === "Enter") {
                    addValue();
                  }
                }}
                placeholder="Add value..."
                className="variation-input"
                style={{ marginBottom: 0 }}
              />
              <img
                src={addIcon}
                alt="Add Product Variant Icon"
                width={18}
                height={18}
                style={{ position: "absolute", right: "5px" }}
                onClick={addValue}
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
};
const ProductVariationEditor = ({ initialProduct, subgroups, onUpdate }) => {
  const [chosenSubgroup, setChosenSubgroup] = useState(null);
  const [variations, setVariations] = useState(
    initialProduct?.variation_categories || []
  );

  // Set initial variations when component mounts
  useEffect(() => {
    if (initialProduct?.variation_categories?.length > 0) {
      setVariations(initialProduct.variation_categories);
    }
  }, [initialProduct]);

  const handleSubgroupChange = useCallback(
    (newSubgroup) => {
      setChosenSubgroup(newSubgroup);
      // Only update variations if the subgroup has them and we don't have initial variations
      if (
        newSubgroup?.variation_categories?.length > 0 &&
        !initialProduct?.variation_categories
      ) {
        setVariations(newSubgroup.variation_categories);
      }
    },
    [initialProduct]
  );

  // Memoize the transformed variations data
  const activeVariations = useMemo(() => {
    return variations.map((variation) => ({
      name: variation.name,
      values: variation.values.map((value) => {
        // If it's already an object with an id and value, return it
        // If it's a string, convert it to an object
        // If it's an empty array, return an empty array
        return typeof value === "object" && value !== null
          ? value
          : value
          ? { id: null, value }
          : value;
      }),
    }));
  }, [variations]);

  // Memoize the update object
  const updateData = useMemo(() => {
    return {
      subgroup: chosenSubgroup?.id,
      subgroupName: chosenSubgroup?.name,
      variation_categories: activeVariations,
    };
  }, [chosenSubgroup, activeVariations]);

  // Use effect with memoized values
  useEffect(() => {
    if (chosenSubgroup) {
      onUpdate?.(updateData);
    }
  }, [updateData, onUpdate]);

  const handleAddVariation = useCallback((newVariation) => {
    setVariations((prev) => [...prev, newVariation]);
  }, []);

  const handleRemoveVariation = useCallback((indexToRemove) => {
    setVariations((prev) => prev.filter((_, index) => index !== indexToRemove));
  }, []);

  const handleUpdateValues = useCallback((updatedVariation, index) => {
    setVariations((prev) => {
      const newVariations = [...prev];
      newVariations[index] = updatedVariation;
      return newVariations;
    });
  }, []);

  return (
    <>
      <style>{styles}</style>
      <div className="variation-container">
        <div className="variation-row-bg">
          <p className="product-detail-text">FU variations</p>
          <div>
            <ProductSubgroupDropdown
              subgroups={subgroups}
              initialSubgroupId={initialProduct?.subgroup}
              onClick={handleSubgroupChange}
            />
          </div>
          <div>
            <VariationSelector
              onAddVariation={handleAddVariation}
              onRemoveVariation={handleRemoveVariation}
              variations={variations}
            />
          </div>
          {variations.map((variation, index) => (
            <div key={variation.id || index}>
              <VariationValues
                variation={variation}
                onUpdateValues={(updated) => handleUpdateValues(updated, index)}
              />
            </div>
          ))}
        </div>
      </div>
    </>
  );
};

export default ProductVariationEditor;
